import { Component, Input, Output, EventEmitter, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../../icons/icon.component';
import type { NavIconName } from '../../../icons/nav-icon.model';

/**
 * MG3P Button Component
 * 
 * Botão reutilizável com múltiplas variantes, tamanhos e estados.
 * Segue padrões de acessibilidade WCAG 2.1.
 * 
 * @example
 * ```html
 * <mg3p-button variant="primary" size="md" (clicked)="handleClick()">
 *   Salvar
 * </mg3p-button>
 * 
 * <mg3p-button variant="secondary" [loading]="isLoading" [disabled]="!isValid">
 *   Processar
 * </mg3p-button>
 * 
 * <mg3p-button variant="danger" icon="🗑️" iconPosition="left">
 *   Excluir
 * </mg3p-button>
 * ```
 */
@Component({
  selector: 'mg3p-button',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <button
      [type]="type"
      [disabled]="disabled || loading"
      [class]="buttonClasses()"
      [attr.aria-busy]="loading"
      [attr.aria-disabled]="disabled || loading"
      [attr.aria-label]="ariaLabel || null"
      (click)="handleClick($event)"
    >
      @if (loading) {
        <span class="mg3p-button__spinner" aria-hidden="true"></span>
        <span class="sr-only">Carregando...</span>
      }

      @if (iconName && iconPosition === 'left' && !loading) {
        <span class="mg3p-button__icon mg3p-button__icon--left" aria-hidden="true">
          <mg3p-icon [name]="iconName" size="sm" />
        </span>
      } @else if (icon && iconPosition === 'left' && !loading) {
        <span class="mg3p-button__icon mg3p-button__icon--left" aria-hidden="true">
          {{ icon }}
        </span>
      }

      <span class="mg3p-button__label">
        <ng-content></ng-content>
      </span>

      @if (iconName && iconPosition === 'right' && !loading) {
        <span class="mg3p-button__icon mg3p-button__icon--right" aria-hidden="true">
          <mg3p-icon [name]="iconName" size="sm" />
        </span>
      } @else if (icon && iconPosition === 'right' && !loading) {
        <span class="mg3p-button__icon mg3p-button__icon--right" aria-hidden="true">
          {{ icon }}
        </span>
      }
    </button>
  `,
  styles: [`
    :host {
      display: inline-block;
    }

    .mg3p-button {
      /* Reset */
      appearance: none;
      border: none;
      background: none;
      margin: 0;
      
      /* Layout */
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-sm);
      
      /* Typography */
      font-family: var(--font-family-sans);
      font-weight: var(--font-weight-semibold);
      line-height: var(--line-height-normal);
      text-decoration: none;
      white-space: nowrap;
      
      /* Interaction */
      cursor: pointer;
      user-select: none;
      transition: all var(--transition-fast);
      
      /* Accessibility */
      position: relative;
    }

    .mg3p-button:focus-visible {
      outline: 3px solid var(--color-border-focus);
      outline-offset: 2px;
    }

    /* ========================================
       SIZES
       ======================================== */
    .mg3p-button--sm {
      padding: var(--space-xs) var(--space-md);
      font-size: var(--font-size-sm);
      border-radius: var(--radius-md);
      min-height: 32px;
    }

    .mg3p-button--md {
      padding: var(--space-sm) var(--space-lg);
      font-size: var(--font-size-base);
      border-radius: var(--radius-md);
      min-height: 40px;
    }

    .mg3p-button--lg {
      padding: var(--space-md) var(--space-xl);
      font-size: var(--font-size-lg);
      border-radius: var(--radius-lg);
      min-height: 48px;
    }

    /* ========================================
       VARIANTS
       ======================================== */
    
    /* Primary */
    .mg3p-button--primary {
      background: var(--color-pmdf-primary);
      color: var(--color-pmdf-primary-foreground);
      box-shadow: var(--shadow-pmdf-sm);
    }

    .mg3p-button--primary:hover:not(:disabled) {
      background: var(--color-pmdf-primary-dark);
      box-shadow: var(--shadow-pmdf-md);
      transform: translateY(-1px);
    }

    .mg3p-button--primary:active:not(:disabled) {
      background: var(--color-pmdf-primary-dark);
      box-shadow: var(--shadow-pmdf-sm);
      transform: translateY(0);
    }

    /* Secondary */
    .mg3p-button--secondary {
      background: var(--color-pmdf-surface);
      color: var(--color-pmdf-text);
      border: 1px solid var(--color-pmdf-border);
      box-shadow: var(--shadow-pmdf-sm);
    }

    .mg3p-button--secondary:hover:not(:disabled) {
      background: var(--color-pmdf-bg-elevated);
      border-color: var(--color-pmdf-primary-muted);
      box-shadow: var(--shadow-pmdf-md);
    }

    .mg3p-button--secondary:active:not(:disabled) {
      background: var(--color-pmdf-primary-soft);
      box-shadow: var(--shadow-pmdf-sm);
    }

    /* Danger */
    .mg3p-button--danger {
      background: var(--color-pmdf-danger);
      color: var(--color-pmdf-primary-foreground);
      border: 1px solid color-mix(in srgb, var(--color-pmdf-danger) 70%, #000);
      box-shadow: var(--shadow-sm, var(--shadow-pmdf-sm));
      font-weight: var(--font-weight-semibold);
    }

    .mg3p-button--danger:hover:not(:disabled) {
      background: color-mix(in srgb, var(--color-pmdf-danger) 85%, #000);
      border-color: color-mix(in srgb, var(--color-pmdf-danger) 70%, #000);
      box-shadow: var(--shadow-pmdf-md);
      transform: translateY(-1px);
    }

    .mg3p-button--danger:active:not(:disabled) {
      background: var(--color-danger-700, #7f1d1d);
      box-shadow: var(--shadow-sm, var(--shadow-pmdf-sm));
      transform: translateY(0);
    }

    .mg3p-button--danger .mg3p-button__icon {
      color: #fff;
    }

    /* Ghost */
    .mg3p-button--ghost {
      background: transparent;
      color: var(--color-pmdf-accent, var(--color-pmdf-text));
      border: 1px solid transparent;
    }

    .mg3p-button--ghost:hover:not(:disabled) {
      background: var(--color-pmdf-bg-elevated, var(--color-bg-elevated));
      color: var(--color-pmdf-text, var(--color-text-primary));
      border-color: var(--color-pmdf-border, var(--color-border-default));
    }

    .mg3p-button--ghost:active:not(:disabled) {
      background: color-mix(in srgb, var(--color-pmdf-primary-soft) 40%, var(--color-pmdf-bg-elevated) 60%);
    }

    /* ========================================
       STATES
       ======================================== */
    
    /* Disabled */
    .mg3p-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
      box-shadow: none !important;
    }

    /* Loading */
    .mg3p-button--loading {
      position: relative;
      color: transparent;
    }

    .mg3p-button--loading .mg3p-button__label {
      visibility: hidden;
    }

    /* Full Width */
    .mg3p-button--full {
      width: 100%;
    }

    /* ========================================
       ELEMENTS
       ======================================== */
    
    .mg3p-button__spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 16px;
      height: 16px;
      border: 2px solid currentColor;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
      to {
        transform: translate(-50%, -50%) rotate(360deg);
      }
    }

    .mg3p-button__icon {
      display: inline-flex;
      align-items: center;
      font-size: 1.2em;
      line-height: 0;
    }

    .mg3p-button__label {
      display: inline-flex;
      align-items: center;
    }

    /* ========================================
       ACCESSIBILITY
       ======================================== */
    
    @media (prefers-reduced-motion: reduce) {
      .mg3p-button,
      .mg3p-button__spinner {
        transition: none;
        animation: none;
      }
    }
  `]
})
export class ButtonComponent {
  /**
   * Variante visual do botão
   */
  @Input() variant: 'primary' | 'secondary' | 'danger' | 'ghost' = 'primary';

  /**
   * Tamanho do botão
   */
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  /**
   * Tipo HTML do botão
   */
  @Input() type: 'button' | 'submit' | 'reset' = 'button';

  /**
   * Estado desabilitado
   */
  @Input() disabled = false;

  /**
   * Estado de carregamento (mostra spinner)
   */
  @Input() loading = false;

  /** @deprecated Preferir iconName */
  @Input() icon?: string;

  /** Ícone SVG (família mg3p-icon) */
  @Input() iconName?: NavIconName;

  @Input() iconPosition: 'left' | 'right' = 'left';

  /** Rótulo acessível quando o botão é só ícone */
  @Input() ariaLabel?: string;

  /**
   * Largura total (100%)
   */
  @Input() fullWidth = false;

  /**
   * Evento de clique
   */
  @Output() clicked = new EventEmitter<MouseEvent>();

  /**
   * Classes CSS computadas
   */
  buttonClasses = computed(() => {
    const classes = [
      'mg3p-button',
      `mg3p-button--${this.variant}`,
      `mg3p-button--${this.size}`,
    ];

    if (this.loading) {
      classes.push('mg3p-button--loading');
    }

    if (this.fullWidth) {
      classes.push('mg3p-button--full');
    }

    return classes.join(' ');
  });

  /**
   * Handler de clique
   */
  handleClick(event: MouseEvent): void {
    if (!this.disabled && !this.loading) {
      this.clicked.emit(event);
    }
  }
}

// Made with Bob
